
export type LanguageCode =
  | 'en'
  | 'pt'
  | 'es'
  | 'de'
  | 'hi'
  | 'id'
  | 'it'
  | 'th'
  | 'zh-cn'
  | 'zh-tw'
  | 'vi'
  | 'tr'
  | 'fr'
  | 'ko'
  | 'ms'
  | 'ru'
  | 'ja'
  | 'ar'
  | 'pl';

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  image: string;
  date: string;
  tags: string[];
  // SEO specific fields
  description?: string;
  keywords?: string[];
}

export enum QRType {
  URL = 'URL',
  TEXT = 'TEXT',
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
  SMS = 'SMS',
  WIFI = 'WIFI',
  VCARD = 'VCARD',
  FACEBOOK = 'FACEBOOK',
  TWITTER = 'TWITTER',
  YOUTUBE = 'YOUTUBE',
  BARCODE = 'BARCODE'
}

export type FrameStyle = 'none' | 'simple' | 'balloon' | 'box' | 'badge' | 'polaroid' | 'phone' | 'header' | 'corner';

export interface QRConfig {
  value: string;
  fgColor: string;
  bgColor: string;
  size: number;
  level: 'L' | 'M' | 'Q' | 'H';
  includeMargin: boolean;
  imageSettings?: {
    src: string;
    height: number;
    width: number;
    excavate: boolean;
  };
  frameStyle: FrameStyle;
  frameText: string;
  frameColor: string;
  frameTextColor: string;
}

type PageMeta = {
  title: string;
  description: string;
  keywords: string[];
};

export interface Translation {
  nav: {
    generator: string;
    scanner: string;
    mobile: string;
    blog: string;
    langSelect: string;
  };
  pageTitles: {
    generator: string;
    scanner: string;
    textToQr: string;
    linkToQr: string;
  };
  pageMeta: {
    generator: PageMeta;
    scanner: PageMeta;
    textToQr: PageMeta;
    linkToQr: PageMeta;
  };
  hero: {
    title: string;
    subtitle: string;
    desc: string;
    ctaPrimary?: string;
    ctaSecondary?: string;
  };
  info: {
    helpTitle: string;
    helpSubtitle: string;
    contactTitle: string;
    contactSubtitle: string;
    privacyTitle: string;
    termsTitle: string;
  };
  contactForm: {
    name: string;
    email: string;
    message: string;
    send: string;
    orEmail: string;
    sending: string;
    successTitle: string;
    successMessage: string;
    sendAnother: string;
    errorMessage: string;
  };
  generator: {
    highlights?: {
      title: string;
      subtitle?: string;
      items: { title: string; desc: string }[];
    };
    sectionContent: string;
    sectionColors: string;
    sectionLogo: string;
    sectionFrame: string;
    sectionDesign: string;
    sectionCorrection: string;
    labelUrl: string;
    labelText: string;
    labelSsid: string;
    labelPassword: string;
    labelEncryption: string;
    labelFgColor: string;
    labelBgColor: string;
    labelFrameText: string;
    labelFrameColor: string;
    placeholder: string;
    placeholders: {
      url: string;
      text: string;
      email: string;
      subject: string;
      message: string;
      phone: string;
      ssid: string;
      password: string;
      barcode: string;
    };
    download: string;
    downloadSvg: string;
    generateButton: string;
    copy: string;
    uploadLogo: string;
    preview: string;
    lowQuality: string;
    highQuality: string;
    foreverFree: string;
    barcodeWarning: string;
    barcodeColors: string;
    frameStyles: {
      none: string;
      simple: string;
      balloon: string;
      box: string;
      badge: string;
      polaroid: string;
      phone: string;
      header: string;
      corner: string;
    };
    vCard: {
      firstName: string;
      lastName: string;
      mobile: string;
      phone: string;
      fax: string;
      email: string;
      company: string;
      job: string;
      street: string;
      city: string;
      zip: string;
      state: string;
      country: string;
      website: string;
    };
    types: Record<QRType, string>;
    guide: {
      title: string;
      steps: string[];
    };
    gettingStarted: {
      title: string;
      steps: { title: string; text: string }[];
    };
    about: {
      title: string;
      subtitle: string;
      features: string[];
      content: { title: string; body: string }[];
    };
    featureSuite?: {
      title: string;
      subtitle: string;
      categories: { title: string; body: string; items: string[] }[];
    };
    trust?: {
      title: string;
      subtitle: string;
      items: string[];
    };
    faq: {
      title: string;
      items: { q: string; a: string }[];
    };
  };
  scanner: {
    title: string;
    subtitle: string;
    cameraPermission: string;
    result: string;
    copy: string;
    copied: string;
    open: string;
    delete: string;
    scanAgain: string;
    privacy: string;
    upload: string;
    paste: string;
    drop: string;
    retry: string;
    errorAccessDenied: string;
    errorCamera: string;
    initializing: string;
    cameraActive: string;
    scannedItems: string;
    recentScans: string;
    clickToUpload: string;
    newBadge: string;
    tabCameraMobile: string;
    tabCameraDesktop: string;
    emptyState: {
        title: string;
        desc: string;
    };
    highlights?: {
      title: string;
      items: { title: string; desc: string }[];
    };
    intro: {
      title: string;
      body: string;
    };
    howTo: {
      title: string;
      methods: { title: string; steps: string[] }[];
    };
    features: {
      title: string;
      items: { title: string; desc: string }[];
    };
    useCases: {
      title: string;
      items: { title: string; desc: string }[];
    };
    faq: {
      title: string;
      items: { q: string; a: string }[];
    };
  };
  blog: {
    title: string;
    subtitle: string;
    readMore: string;
    back: string;
    notFound: string;
  };
  common: {
    footer: string;
    privacy: string;
    terms: string;
    contact: string;
    scanToOpen: string;
    help: string;
    free: string;
    product: string;
    resources: string;
    company: string;
    api: string;
    systemStatus: string;
    footerDesc: string;
  };
}
